import React from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import Image from "../../Image";
import LinkComponent, { Link } from "../../LinkComponent";
import Button from "../../NewForms/Button";
import HeadlineItem from "../ContentElement/HeadlineItem";
import { replaceMarkdownBreak, SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";

const headlineBackgroundColorMap = {
    blue: (theme) => theme.colors.blue.toString(),
    red: (theme) => theme.colors.red.toString(),
};

const BlueBox = styled.div`
    background-color: ${({ theme, bgColor }) =>
        headlineBackgroundColorMap[bgColor]?.(theme) ??
        theme.colors.blue.toString()};
    overflow: hidden;

    color: ${({ theme }) => theme.colors.white.toString()}!important;
    padding: 20px 40px;
    max-width: 100%;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;

    ${({ theme }) => theme.breakpoints.from.small.css`
        margin: 50px;
        border-radius: 20px;
        max-width: 600px;
    `}
`;

const Container = styled.div`
    position: relative;
    width: 100%;

    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) => theme.paddings.containerOuter.right}
`;

const HeadlineContainer = styled.div`
    width: 100%;
    ${({ theme }) => theme.breakpoints.from.small.css`
        z-index: 2;
        padding-right: 0;
        width: unset;
        ${theme.paddings.containerOuter.left}
        left: 0;
        top: 0;
        position: absolute;
    `}
`;

const ImgContainer = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;

    max-height: 500px;
    overflow: hidden;

    @media (min-width: 800px) {
        max-height: 400px;
    }

    @media (min-width: 1000px) {
        max-height: 500px;
    }

    @media (min-width: 1400px) {
        max-height: 600px;
    }

    @media (min-width: 2000px) {
        max-height: 700px;
    }

    @media (min-width: 2400px) {
        max-height: 600px;
    }

    @media (min-width: 2700px) {
        max-height: 700px;
    }

    .gatsby-image-wrapper {
        width: 100%;
        height: 100%;
        border-top-left-radius: 20px;
        border-top-right-radius: 20px;
        overflow: hidden;
    }
    ${({ theme }) => theme.breakpoints.from.small.css`
        height: 100%;
        .gatsby-image-wrapper {
            border-radius: 20px;
        }
    `}
`;

const NonCmsImgDummy = styled.div`
    width: 100%;
    height: 100%;
`;

const CtaContainer = styled.div`
    display: flex;

    ${({ theme }) => theme.breakpoints.from.small.css`
        display: inline-flex;
    `};
`;
const StyledDiv = styled.div`
    ${({ theme }) => theme.breakpoints.from.small.css`
        height: 100%;
    `};
`;
const ConditionalLink = ({ cta, children }) =>
    cta?.href ? (
        <Link to={cta.href}>{children}</Link>
    ) : (
        <StyledDiv>{children}</StyledDiv>
    );

const ImgShapeContainer = ({
    text,
    image,
    link,
    imgPositionX = "center",
    imgPositionY = "center",
    isSmallShape = false,
    hasTextOutline = true,
    side = "right",
    headlineSize = "2.5rem",
    headlineNumber,
    gtag = undefined,
    imageMirroring = true,
    nonCmsImageSrc = undefined,
    cta = undefined,
    headlineBackgroundColor = "blue",
    subline,
    ...props
}) => {
    const theme = useTheme();
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);

    const sublineCss = css`
        margin-bottom: 24px;

        font-size: 1.2rem;
        line-height: 1.4;
        font-weight: 600;

        color: ${theme.colors.white.toString()};

        ${theme.breakpoints.upTo.small.css`
        font-size: 0.95rem;
        margin-bottom: 16px;
    `}
    `;

    const headlineCss = css`
        font-weight: 900;
        color: ${theme.colors.white.toString()}!important;
        word-wrap: normal;
        -webkit-text-stroke: 0 !important;
        text-shadow: none !important;
        line-height: initial;

        ${theme.breakpoints.upTo.small.css`
            line-height: 2.75rem;

            br {
              display: none;
            }

        `}
        ${theme.breakpoints.upTo.extraSmall.css`
            line-height: 2.5rem;
        `}
    `;

    return (
        <Container {...props} id="element-img-shape">
            <ConditionalLink cta={cta}>
                <ImgContainer
                    css={css`
                        background-repeat: no-repeat;
                        background-position-x: ${imgPositionX};
                        background-position-y: ${imgPositionY};
                        background-size: cover;

                        img {
                            object-fit: cover;
                            object-position: ${imgPositionX} ${imgPositionY};
                        }

                        ${nonCmsImageSrc &&
                        `background-image: url("${nonCmsImageSrc}")`}
                    `}
                >
                    {nonCmsImageSrc ? <NonCmsImgDummy /> : <Image {...image} />}
                </ImgContainer>
            </ConditionalLink>
            <HeadlineContainer>
                <div>
                    <SideNavContainer paddingLeft={paddingLeft}>
                        <BlueBox bgColor={headlineBackgroundColor}>
                            <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                `}
                            >
                                {text && (
                                    <>
                                        {theme.breakpoints.upTo.medium.useMatches() && (
                                            <HeadlineItem
                                                color="white"
                                                css={headlineCss}
                                                headline={headlineNumber}
                                                fontSize="2.75rem"
                                                fSNormal="2.75rem"
                                                fSMedium="2.5rem"
                                                fSSmall="1.9rem"
                                                fSExtraSmall="1.6rem"
                                                hasTextOutline={false}
                                            >
                                                {replaceMarkdownBreak(text)}
                                            </HeadlineItem>
                                        )}

                                        {theme.breakpoints.from.normal.useMatches() && (
                                            <HeadlineItem
                                                color="white"
                                                css={headlineCss}
                                                headline={headlineNumber}
                                                fontSize="2.75rem"
                                                fSNormal="2.75rem"
                                                fSMedium="2.5rem"
                                                fSSmall="1.9rem"
                                                fSExtraSmall="1.6rem"
                                                hasTextOutline={false}
                                            >
                                                {text}
                                            </HeadlineItem>
                                        )}
                                    </>
                                )}

                                {subline && (
                                    <div css={sublineCss}>{subline}</div>
                                )}

                                {link && (
                                    <LinkComponent
                                        icon="arrow-right"
                                        link={link}
                                        {...(gtag ? { "data-gtag": gtag } : {})}
                                        css={css`
                                            color: white !important;
                                            text-decoration: underline !important;
                                        `}
                                    />
                                )}
                                {cta && cta.href && (
                                    <CtaContainer>
                                        <Button
                                            to={cta.href}
                                            css={css`
                                                text-decoration: underline;
                                                padding: 0 0 26px 0;
                                                border: 0;
                                                background: transparent;
                                            `}
                                        >
                                            {cta.text}
                                        </Button>
                                    </CtaContainer>
                                )}
                            </div>
                        </BlueBox>
                    </SideNavContainer>
                </div>
            </HeadlineContainer>
        </Container>
    );
};

export default ImgShapeContainer;
